import { generateIdempotencyKey } from './idempotency';
import { LoctopusApiError, LoctopusNetworkError } from './errors';
import {
  normalizeCheckoutSession,
  normalizeEventDetail,
  normalizeOrder,
  normalizeSeatMap,
  normalizeTicket,
} from './normalizeEvent';
import type { PaginatedResponse, PaginationParams } from './pagination';
import type {
  CheckoutSession,
  CreateCheckoutSessionInput,
  EventDetail,
  EventSummary,
  Order,
  OrderResendResult,
  PromoCodeValidationInput,
  PromoCodeValidationResult,
  SeatMap,
  Ticket,
  TransferTicketInput,
} from './types';

export interface LoctopusClientConfig {
  /** e.g. "https://api.loctopusweb.com" - no trailing slash required. */
  baseUrl: string;
  /** Browser-safe key (`lw_pk_{test|live}_...`). Never pair this with a secretKey client-side. */
  publicKey?: string;
  /** Server-side only (`lw_sk_{test|live}_...`). Never bundle this into a browser build. */
  secretKey?: string;
  /** Override for non-global-fetch environments (tests, older runtimes). Defaults to `fetch`. */
  fetch?: typeof fetch;
}

/** Per-call auth override, for routes docs/API.md marks "secret or buyer session" when called
 *  from a public, secret-key-less context (e.g. `apps/checkout` fetching a buyer's own order).
 *  docs/API.md does not yet document how a buyer session is issued/shaped (login endpoint isn't
 *  in the public route list) - this is a deliberately generic hook for whatever that turns out
 *  to be (bearer token today; swap the request-building if it's cookie-based instead). */
export interface AuthOverride {
  bearerToken: string;
}

export interface MutationOptions {
  idempotencyKey?: string;
  auth?: AuthOverride;
  signal?: AbortSignal;
}

export interface ReadOptions {
  auth?: AuthOverride;
  signal?: AbortSignal;
  /** Guest-buyer access token (from `createCheckoutSession`'s response) - the only auth a buyer
   * with no API key/session has. Only `getCheckoutSession` sends this today; apps/api's GET
   * /v1/checkout-sessions/:id checks it as a query param, not a bearer header, so it can't go
   * through `AuthOverride`. */
  accessToken?: string;
}

type HttpMethod = 'GET' | 'POST' | 'PATCH';

interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  idempotencyKey?: string;
  auth?: AuthOverride;
  signal?: AbortSignal;
}

/**
 * Typed client for the Loctopus public API (docs/API.md). The only supported way for
 * `apps/checkout`, `apps/widget` and any `clients/{slug}` custom front end to reach the backend -
 * never import `apps/api` or the DB directly (docs/CUSTOM_CLIENT_BUILDS.md).
 */
export class LoctopusClient {
  private readonly baseUrl: string;
  private readonly publicKey?: string;
  private readonly secretKey?: string;
  private readonly fetchImpl: typeof fetch;

  constructor(config: LoctopusClientConfig) {
    if (!config.baseUrl) {
      throw new TypeError(
        'LoctopusClient requires { baseUrl }, e.g. "https://api.loctopusweb.com" - got an empty value.',
      );
    }
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.publicKey = config.publicKey;
    this.secretKey = config.secretKey;
    const resolvedFetch = config.fetch ?? globalThis.fetch;
    if (!resolvedFetch) {
      throw new TypeError(
        'No global `fetch` is available in this runtime. Pass { fetch } explicitly to LoctopusClient.',
      );
    }
    // Real bug found via a live browser run (not just a lint/type issue): storing the bare
    // `globalThis.fetch` reference and later invoking it as `this.fetchImpl(...)` detaches it from
    // `window`, and the native fetch implementation throws "Illegal invocation" when its receiver
    // isn't `window`/`globalThis`. Binding here fixes every consumer (apps/checkout, apps/widget)
    // at the source instead of each one needing its own `{ fetch: (...a) => fetch(...a) }`
    // workaround. A caller-supplied `config.fetch` is left as-is - it may already be correctly
    // bound, and re-binding an arbitrary function the caller controls isn't this constructor's call.
    this.fetchImpl = config.fetch ?? resolvedFetch.bind(globalThis);
  }

  listEvents(
    params?: PaginationParams,
    options?: ReadOptions,
  ): Promise<PaginatedResponse<EventSummary>> {
    return this.request('GET', '/v1/events', {
      query: { cursor: params?.cursor, limit: params?.limit },
      auth: options?.auth,
      signal: options?.signal,
    });
  }

  async getEvent(id: string, options?: ReadOptions): Promise<EventDetail> {
    const raw = await this.request<unknown>('GET', `/v1/events/${encodeURIComponent(id)}`, {
      auth: options?.auth,
      signal: options?.signal,
    });
    return normalizeEventDetail(raw);
  }

  /**
   * docs/API.md defines this route as event-scoped only (`/v1/events/:id/seat-map`), with no
   * session query param documented. An optional `sessionId` is accepted here because seat
   * *holds* are session-scoped in the inventory Durable Object (`packages/core`) even though the
   * seat map geometry is defined once per event - omit it if apps/api turns out to need the
   * session resolved some other way (e.g. a separate endpoint).
   */
  async getSeatMap(
    eventId: string,
    params?: { sessionId?: string },
    options?: ReadOptions,
  ): Promise<SeatMap> {
    const raw = await this.request<unknown>(
      'GET',
      `/v1/events/${encodeURIComponent(eventId)}/seat-map`,
      { query: { sessionId: params?.sessionId }, auth: options?.auth, signal: options?.signal },
    );
    return normalizeSeatMap(raw);
  }

  /**
   * Real bug found live in a browser: this used to send `input` straight through as the request
   * body, but `CreateCheckoutSessionInput`'s field names (`lineItems`, `buyer: {email,...}`,
   * `customQuestionAnswers`) were never reconciled against the real
   * `createCheckoutSessionSchema` (apps/api/src/routes/public/schemas.ts), which wants `items`
   * (one entry per seat for reserved seating, not a `seatIds` array per ticket type), a flat
   * `buyerEmail`/`buyerPhone`, and `answers` with `customQuestionId`/`answerText`. Every real
   * checkout attempt through apps/checkout failed `400 validation_failed` until this translation
   * was added - the buyer-facing purchase flow had never actually worked. `firstName`/`lastName`
   * have no server-side field yet, so they're intentionally dropped here, not sent anywhere.
   */
  async createCheckoutSession(
    input: CreateCheckoutSessionInput,
    options?: MutationOptions,
  ): Promise<CheckoutSession> {
    const items = input.lineItems.flatMap((li) =>
      li.seatIds && li.seatIds.length > 0
        ? li.seatIds.map((seatId) => ({ ticketTypeId: li.ticketTypeId, seatId, quantity: 1 }))
        : [{ ticketTypeId: li.ticketTypeId, quantity: li.quantity }],
    );
    const body = {
      eventId: input.eventId,
      sessionId: input.sessionId,
      items,
      addOns: (input.addOns ?? []).map((a) => ({
        addOnId: a.addOnId,
        quantity: a.quantity,
        amountMinor: a.amount?.amountMinor,
      })),
      promoCode: input.promoCode,
      buyerEmail: input.buyer.email,
      buyerPhone: input.buyer.phone,
      answers: (input.customQuestionAnswers ?? []).map((a) => ({
        customQuestionId: a.questionId,
        answerText: a.answer,
      })),
      turnstileToken: input.turnstileToken,
    };
    const raw = await this.request<unknown>('POST', '/v1/checkout-sessions', {
      body,
      idempotencyKey: options?.idempotencyKey,
      auth: options?.auth,
      signal: options?.signal,
    });
    return normalizeCheckoutSession(raw);
  }

  async getCheckoutSession(id: string, options?: ReadOptions): Promise<CheckoutSession> {
    const raw = await this.request<unknown>('GET', `/v1/checkout-sessions/${encodeURIComponent(id)}`, {
      query: { accessToken: options?.accessToken },
      auth: options?.auth,
      signal: options?.signal,
    });
    return normalizeCheckoutSession(raw);
  }

  validatePromoCode(
    input: PromoCodeValidationInput,
    options?: ReadOptions,
  ): Promise<PromoCodeValidationResult> {
    return this.request('POST', '/v1/promo-codes/validate', {
      body: input,
      auth: options?.auth,
      signal: options?.signal,
    });
  }

  // REAL BUG FOUND AND FIXED: this never sent `options.accessToken` as a query param (unlike
  // `getCheckoutSession` above, which does), so a real guest buyer - who only ever has an
  // accessToken, never a secret key or auth override - got a bare 401 from apps/storefront-api's
  // `GET /v1/orders/:id` (requireBuyerAccessToken) on every call. No guest buyer's confirmation
  // page could ever have worked. Fixed the same way `getCheckoutSession` already does it.
  // Also now runs the real response through `normalizeOrder` - forwarding the accessToken alone
  // was not sufficient, since `getOrder` had no normalizer at all and every field a consumer
  // reads (`order.status`, `order.ticketIds`, ...) would still have been `undefined` against the
  // real `{order, items, tickets}` nested response (see normalizeEvent.ts's doc comment).
  async getOrder(id: string, options?: ReadOptions): Promise<Order> {
    const raw = await this.request<unknown>('GET', `/v1/orders/${encodeURIComponent(id)}`, {
      query: { accessToken: options?.accessToken },
      auth: options?.auth,
      signal: options?.signal,
    });
    return normalizeOrder(raw);
  }

  // Same real bug as `getOrder` above, same fix, same reasoning (normalizeTicket). A ticket's own
  // accessToken (distinct from its order's - a different HMAC input, never interchangeable) comes
  // from `Order.ticketAccessTokens[ticketId]`, not the order's own accessToken.
  async getTicket(id: string, options?: ReadOptions): Promise<Ticket> {
    const raw = await this.request<unknown>('GET', `/v1/tickets/${encodeURIComponent(id)}`, {
      query: { accessToken: options?.accessToken },
      auth: options?.auth,
      signal: options?.signal,
    });
    return normalizeTicket(raw);
  }

  /** docs/API.md marks this secret-key only - never call it from a browser build with a secret
   *  key attached; it exists on the client for server-side/dashboard callers. */
  resendOrder(orderId: string, options?: MutationOptions): Promise<OrderResendResult> {
    return this.request('POST', `/v1/orders/${encodeURIComponent(orderId)}/resend`, {
      idempotencyKey: options?.idempotencyKey,
      auth: options?.auth,
      signal: options?.signal,
    });
  }

  transferTicket(
    ticketId: string,
    input: TransferTicketInput,
    options?: MutationOptions,
  ): Promise<Ticket> {
    return this.request('POST', `/v1/tickets/${encodeURIComponent(ticketId)}/transfer`, {
      body: input,
      idempotencyKey: options?.idempotencyKey,
      auth: options?.auth,
      signal: options?.signal,
    });
  }

  private async request<T>(method: HttpMethod, path: string, options: RequestOptions): Promise<T> {
    const url = new URL(this.baseUrl + path);
    if (options.query) {
      for (const [key, value] of Object.entries(options.query)) {
        if (value !== undefined) url.searchParams.set(key, String(value));
      }
    }

    const headers: Record<string, string> = { Accept: 'application/json' };
    const isMutating = method === 'POST' || method === 'PATCH';

    const bearer = options.auth?.bearerToken ?? this.secretKey ?? this.publicKey;
    if (bearer) {
      headers.Authorization = `Bearer ${bearer}`;
    }

    if (isMutating) {
      headers['Idempotency-Key'] = options.idempotencyKey ?? generateIdempotencyKey();
    }

    let body: string | undefined;
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url.toString(), {
        method,
        headers,
        body,
        signal: options.signal,
      });
    } catch (cause) {
      throw new LoctopusNetworkError(url.toString(), cause);
    }

    const text = await response.text();
    const parsedJson = text.length > 0 ? safeJsonParse(text) : undefined;

    if (!response.ok) {
      throw LoctopusApiError.fromResponse(response.status, parsedJson, text, url.toString());
    }

    return parsedJson as T;
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
